import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { setResolvedTheme, setThemeMode, ThemeMode } from '../store/slices/themeSlice';

/**
 * Hook to manage theme state and system preference detection.
 *
 * - Listens for system color scheme changes when mode is 'system'
 * - Applies data-theme attribute to document for CSS variables
 * - Provides functions to change theme mode
 */
export const useTheme = () => {
  const dispatch = useAppDispatch();
  const { mode, resolvedTheme } = useAppSelector((state) => state.theme);

  // Listen for system preference changes
  useEffect(() => {
    if (mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      dispatch(setResolvedTheme(e.matches ? 'dark' : 'light'));
    };

    // Check initial state
    dispatch(setResolvedTheme(mediaQuery.matches ? 'dark' : 'light'));

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [mode, dispatch]);

  // Apply data-theme attribute to document for CSS variables
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  const changeTheme = (newMode: ThemeMode) => {
    dispatch(setThemeMode(newMode));
  };

  const toggleTheme = () => {
    // Simple toggle between light and dark (skips system)
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    dispatch(setThemeMode(newTheme));
  };

  return {
    mode,
    resolvedTheme,
    isDarkMode: resolvedTheme === 'dark',
    changeTheme,
    toggleTheme,
  };
};

export default useTheme;
